import type { groupResponsibleStore } from '../infrastructure/store'
import type { GroupResponsibleTabName } from './types'
import type { Locale } from '@/intl/types'
import type QsClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import groupResponsibleAPIClient from '../infrastructure/groupResponsibleAPIClient'
import { computeRoomSwapAssignments, getRoomBookingCustomerIds, getUnassignedParticipantIds } from './roomSwap'

export interface GroupResponsibleUseCases {
  apiClient: typeof groupResponsibleAPIClient
  store: typeof groupResponsibleStore
  queryStringClient: typeof QsClient
  locale?: Locale
}

function groupResponsibleUseCases({
  apiClient,
  store,
  queryStringClient,
  locale,
}: GroupResponsibleUseCases) {
  async function pageLoaded() {
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    const state = store.getState()
    state.setIn('retrieveBookingData.loading', true)
    let data
    try {
      data = await apiClient.getGroupBookingData(masterBookingNumber || '', locale)
      if (!data) {
        state.setIn('retrieveBookingData', { loading: false, error: true })
        return
      }
    }
    catch {
      state.setIn('retrieveBookingData', { loading: false, error: true })
      return
    }
    state.setIn('retrieveBookingData.loading', false)

    state.initialize({
      bookingData: {
        group: {
          name: data?.group.name ?? '',
          imgSrc: data?.group.imgSrc ?? '',
          location: data?.group.location ?? '',
          period: data?.group.period ?? '',
          startingDate: data?.group.startingDate ?? '',
          endingDate: data?.group.endingDate ?? '',
          publicationCode: data?.group.publicationCode ?? '',
          subscriptionModel: data?.group.subscriptionModel ?? 'Open',
          isBlocked: data?.group.isBlocked ?? false,
          masterBookingNumber: data?.group.masterBookingNumber,
        },
      },
      participants: data?.participants,
      rooms: data?.rooms,
      buses: data?.buses,
      roomAssignmentStatus: data?.group.isBlocked ? 'processing' : 'idle',
    })
  }

  async function roomAssignmentStatusRefreshed() {
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    const state = store.getState()
    const previousStatus = state.roomAssignmentStatus

    state.setIn('roomAssignmentStatus', 'refreshing')

    try {
      const data = await apiClient.getGroupBookingData(masterBookingNumber || '', locale)
      if (!data?.group) {
        state.setIn('roomAssignmentStatus', 'error')
        return
      }

      const isServerBlocked = data.group.isBlocked

      if (isServerBlocked) {
        state.setIn('roomAssignmentStatus', 'processing')
        state.setIn('bookingData.group.isBlocked', true)
        state.setIn('rooms', data.rooms)
        state.setIn('participants', data.participants)
        state.setIn('buses', data.buses)
      }
      else if (previousStatus === 'processing' || previousStatus === 'pending') {
        state.setIn('roomAssignmentStatus', 'success')
        state.setIn('bookingData.group.isBlocked', false)
        state.setIn('rooms', data.rooms)
        state.setIn('participants', data.participants)
        state.setIn('buses', data.buses)
        state.discardStagedRoomAssignments()
      }
      else {
        state.setIn('roomAssignmentStatus', 'idle')
        state.setIn('bookingData.group.isBlocked', false)
        state.setIn('rooms', data.rooms)
        state.setIn('participants', data.participants)
        state.setIn('buses', data.buses)
      }
    }
    catch {
      state.setIn('roomAssignmentStatus', 'error')
    }
  }

  function roomAssignmentSuccessBannerDismissed() {
    store.getState().setIn('roomAssignmentStatus', 'idle')
  }

  function tabSelected(tab: GroupResponsibleTabName) {
    store.getState().changeTab(tab)

    const params = new URLSearchParams(queryStringClient.get())
    params.set('activeTab', tab)
    queryStringClient.set(params.toString())
  }

  async function loadBusPickUpPoints() {
    const state = store.getState()
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')

    state.setIn('retrieveBusPointsData', { loading: true, error: false })
    let data
    try {
      data = await apiClient.getGroupBookingBusPoints(masterBookingNumber || '')
      if (!data) {
        state.setIn('retrieveBusPointsData', { loading: false, error: true })
        return
      }
    }
    catch {
      state.setIn('retrieveBusPointsData', { loading: false, error: true })
      return
    }

    state.setIn('retrieveBusPointsData', { loading: false, error: false })
    state.setIn('busPickupPoints', data.pickUpPoints)
    state.setIn('busReturnPoints', data.returnPoints)
    state.setIn('busAssignment.sameAsPickup', data.allPickupPointsMatchedReturnPoints ?? false)
  }

  async function assignBusToParticipants() {
    const state = store.getState()
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    let { selectedPickupPointId, selectedReturnPointId, sameAsPickup } = state.busAssignment

    if (!selectedPickupPointId && state.busPickupPoints.length === 1) {
      selectedPickupPointId = state.busPickupPoints[0].id
      state.setIn('busAssignment.selectedPickupPointId', selectedPickupPointId)
    }

    if (!selectedReturnPointId && state.busReturnPoints.length === 1) {
      selectedReturnPointId = state.busReturnPoints[0].id
      state.setIn('busAssignment.selectedReturnPointId', selectedReturnPointId)
    }

    const pickupError = state.busPickupPoints.length > 1 && !selectedPickupPointId
    const returnError = !sameAsPickup && state.busReturnPoints.length > 1 && !selectedReturnPointId

    state.setIn('busAssignment.pickupPointNotSelected', pickupError)
    state.setIn('busAssignment.returnPointNotSelected', returnError)

    if (pickupError || returnError) {
      return
    }

    state.setIn('busAssignment.submitting', true)
    state.setIn('busAssignment.error', false)

    try {
      const participantIds = Array.from(state.selectedParticipantIds)
      await apiClient.assignBusToParticipants({
        masterBookingNumber: Number(masterBookingNumber),
        publicationCode: state.bookingData.group.publicationCode,
        participantsIds: participantIds.map(id => ({
          bookingCustomerId: Number(Object.values(state.participants).find(d => d.id === id)?.bookingCustomerId),
          childBookingNumber: Object.values(state.participants).find(d => d.id === id)?.bookingNumber,
        })),
        pickupPointId: selectedPickupPointId,
        returnPointId: selectedReturnPointId,
      })
      participantIds.forEach((id) => {
        state.setIn(`participants.${id}.isBusAssignmentProcessing`, true)
      })
      state.setIn('selectedParticipantIds', new Set())
      state.setIn('busAssignment', {
        selectedPickupPointId: '',
        selectedReturnPointId: '',
        sameAsPickup: true,
        submitting: false,
        error: false,
        pickupPointNotSelected: false,
        returnPointNotSelected: false,
      })

      state.toggleIsEditingParticipants(false)
    }
    catch {
      state.setIn('busAssignment.error', true)
      state.setIn('busAssignment.submitting', false)
    }
  }

  async function assignRoomsViaSwap() {
    const state = store.getState()
    const masterBookingNumber = queryStringClient.getValue('bookingNumber')
    const roomId = state.activeExtraInfoId
    const selectedIds = Array.from(state.selectedRoomParticipantIds)
    const hasStaged = Boolean(state.stagedRoomAssignments && Object.keys(state.stagedRoomAssignments).length > 0)

    if (!hasStaged && !roomId) {
      return
    }

    let swappedRooms: Record<string, number[]>

    if (hasStaged && state.stagedRoomAssignments) {
      swappedRooms = state.stagedRoomAssignments

      if (getUnassignedParticipantIds(state.rooms, swappedRooms).length > 0) {
        return
      }
    }
    else {
      if (!roomId) {
        return
      }
      const room = state.rooms[roomId]
      const currentBookingCustomerIds = getRoomBookingCustomerIds(room)
      const hasChanges
        = selectedIds.length !== currentBookingCustomerIds.length
          || selectedIds.some(id => !currentBookingCustomerIds.includes(Number(id)))

      if (!hasChanges && !room.needsAction) {
        state.closeRoomAssignment()
        return
      }
      swappedRooms = computeRoomSwapAssignments(state.rooms, roomId, selectedIds.map(Number))
    }

    state.setIn('roomAssignmentStatus', 'submitting')

    try {
      const payload = buildRoomSwapPayload(masterBookingNumber || '', state.bookingData.group.publicationCode, swappedRooms)

      await apiClient.assignRoomsViaSwap(payload)

      state.setIn('roomAssignmentStatus', 'pending')
      state.setIn('bookingData.group.isBlocked', true)
      if (!hasStaged) {
        state.closeRoomAssignment()
      }
    }
    catch {
      state.setIn('roomAssignmentStatus', 'error')
    }
  }

  return {
    pageLoaded,
    roomAssignmentStatusRefreshed,
    roomAssignmentSuccessBannerDismissed,
    tabSelected,
    loadBusPickUpPoints,
    assignBusToParticipants,
    assignRoomsViaSwap,
  }
}

export default groupResponsibleUseCases

function buildRoomSwapPayload(
  masterBookingNumber: string,
  publicationCode: string,
  swappedRooms: Record<string, number[]>,
) {
  return {
    masterBookingNumber,
    publicationCode,
    targetRooms: Object.entries(swappedRooms).map(([bookingNumber, assignedBookingCustomerIds]) => ({
      bookingNumber,
      assignedBookingCustomerIds,
    })),
  }
}
